import scrollIntoView from 'scroll-into-view-if-needed';
import type { CustomScrollBehaviorCallback } from 'scroll-into-view-if-needed/typings/types';

export type KeyMap = {
  up: number;
  down: number;
  left: number;
  right: number;
  enter: number;
  back: number;
};

type Container = {
  name: string;
  selector?: string;
  alignment?: 'horizontal' | 'vertical' | 'no-aligment';
  parent?: string;
};

type OverrideFunction = (
  nodes: Record<string, HTMLElement[]>,
  containers: HTMLElement[],
  next: () => boolean | void,
) => boolean | void;

type NavigationOverride = {
  [K in keyof KeyMap]: OverrideFunction[];
};

export class SpatialFocusService {
  public lastFocused: HTMLElement | undefined = undefined;

  private readonly nodes: Record<string, HTMLElement[]> = {};
  private readonly containers: Map<HTMLElement, Container> = new Map();
  private readonly navOverrides: NavigationOverride = {
    left: [],
    right: [],
    up: [],
    down: [],
    enter: [],
    back: [],
  };
  private readonly scrollBehavior: CustomScrollBehaviorCallback<void> | undefined;

  constructor(map: KeyMap, scrollBehavior?: CustomScrollBehaviorCallback<void>) {
    const navigationKeys = Object.values(map) as number[];
    this.scrollBehavior = scrollBehavior;

    window.addEventListener('keydown', (e: KeyboardEvent) => {
      if (!navigationKeys.includes(e.keyCode)) {
        return;
      }

      e.preventDefault();

      if (e.keyCode === map.left) {
        this.left();
      } else if (e.keyCode === map.right) {
        this.right();
      } else if (e.keyCode === map.up) {
        this.up();
      } else if (e.keyCode === map.down) {
        this.down();
      } else if (e.keyCode === map.enter) {
        this.enter();
      } else if (e.keyCode === map.back) {
        this.back();
      }
    });
  }

  public addElement(node: HTMLElement, container: string): boolean {
    node.dataset.spatialContainer = container;
    node.focus = focusAndScrollTo(node.focus, this.scrollBehavior);
    if (node.tabIndex === -1) {
      node.tabIndex = 0;
    }

    if (!this.nodes[container]) {
      this.nodes[container] = [];
    }

    this.nodes[container].push(node);

    return true;
  }

  public addContainer(node: HTMLElement, containerParam: string | Container): boolean {
    if (node.tabIndex === -1) {
      node.tabIndex = 0;
    }

    const container = typeof containerParam === 'string'
      ? { name: containerParam }
      : containerParam;

    if (container.selector) {
      node.querySelectorAll(container.selector).forEach((child) => {
        this.addElement(child as HTMLElement, container.name);
      });
    } else {
      Array.from(node.children).forEach((child) => {
        this.addElement(child as HTMLElement, container.name);
      });
    }

    this.containers.set(node, container);

    return true;
  }

  public removeElement(node: HTMLElement, container: string): void {
    const removeNodeIndex = this.nodes[container].indexOf(node);
    this.nodes[container].splice(removeNodeIndex, 1);
  }

  public removeContainer(node: HTMLElement): void {
    const container = this.containers.get(node);

    if (container == null) { return; }

    this.containers.delete(node);

    if (container.selector) {
      node.querySelectorAll(container.selector).forEach((child) => {
        this.removeElement(child as HTMLElement, container.name);
      });
    } else {
      Array.from(node.children).forEach((child) => {
        this.removeElement(child as HTMLElement, container.name);
      });
    }
  }

  public left(): void {
    if (this.checkGlobalOverride('left')) { return; }
    this.directionAction(this.leftClosestNode);
  }

  public right(): void {
    if (this.checkGlobalOverride('right')) { return; }
    this.directionAction(this.rightClosestNode);
  }

  public up(): void {
    if (this.checkGlobalOverride('up')) { return; }
    this.directionAction(this.upClosestNode);
  }

  public down(): void {
    if (this.checkGlobalOverride('down')) { return; }
    this.directionAction(this.downClosestNode);
  }

  public enter(): void {
    if (this.checkGlobalOverride('enter')) { return; }
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.click();
    }
  }

  public back(): void {
    if (this.checkGlobalOverride('back')) { return; }
  }

  public pushGlobalOverride(navigationKey: keyof KeyMap, override: OverrideFunction): void {
    this.navOverrides[navigationKey].push(override);
  }

  public popGlobalOverride(navigationKey: keyof KeyMap): void {
    this.navOverrides[navigationKey].pop();
  }

  private checkGlobalOverride(navigationKey: keyof KeyMap, depth: number = 1): boolean | void {
    const navOverrides = this.navOverrides[navigationKey];

    const next = () => this.checkGlobalOverride(navigationKey, depth + 1);

    return navOverrides[navOverrides.length - depth]?.(this.nodes, Array.from(this.containers.keys()), next);
  }

  private setFocusNode(elm: HTMLElement): void {
    elm.focus({preventScroll: true});
  }

  private directionAction(closestFunction: (activeElement: HTMLElement, nodes: HTMLElement[]) => HTMLElement | undefined): void {
    if (document.activeElement instanceof HTMLElement) {

      if (!document.activeElement.dataset.spatialContainer) {
        return;
      }

      this.lastFocused = document.activeElement;

      const closestElement = closestFunction.apply(
        this,
        [
          document.activeElement,
          this.nodes[document.activeElement.dataset.spatialContainer],
        ],
      );

      if (closestElement) {
        this.setFocusNode(closestElement);
      } else {
        const closestContainer = closestFunction.apply(
          this,
          [document.activeElement, Array.from(this.containers.keys())],
        );

        if (closestContainer) {
          this.setFocusNode(closestContainer);
        }
      }
    }
  }

  private leftClosestNode(activeElement: HTMLElement, nodes: HTMLElement[]): HTMLElement | undefined {
    const [x, y] = this.getElementCenter(activeElement);

    const mapped = nodes
      .map((e) => {
        return {
          node: e,
          bounds: e.getBoundingClientRect(),
        };
      })
      .filter((e) => e.node !== activeElement)
      .filter((e) => x >= e.bounds.right)
      .filter((e) => y >= e.bounds.top && y <= e.bounds.bottom)
      .map((e) => {
        return {
          elm: e.node as HTMLElement,
          distance: Math.abs(e.bounds.right - x),
        };
      });

    mapped.sort((a, b) => b.elm.tabIndex - a.elm.tabIndex || a.distance - b.distance);

    if (mapped.length !== 0) {
      return mapped[0].elm;
    }

    return;
  }

  private rightClosestNode(activeElement: HTMLElement, nodes: HTMLElement[]): HTMLElement | undefined {
    const [x, y] = this.getElementCenter(activeElement);

    const mapped = nodes
      .map((e) => {
        return {
          node: e,
          bounds: (e as HTMLElement).getBoundingClientRect(),
        };
      })
      .filter((e) => e.node !== activeElement)
      .filter((e) => x <= e.bounds.left)
      .filter((e) => y >= e.bounds.top && y <= e.bounds.bottom)
      .map((e) => {
        return {
          elm: e.node as HTMLElement,
          distance: Math.abs(e.bounds.left - x),
        };
      });

    mapped.sort((a, b) => b.elm.tabIndex - a.elm.tabIndex || a.distance - b.distance);

    if (mapped.length !== 0) {
      return mapped[0].elm;
    }

    return;
  }

  private upClosestNode(activeElement: HTMLElement, nodes: HTMLElement[]): HTMLElement | undefined {
    const [x, y] = this.getElementCenter(activeElement);

    const mapped = nodes
      .map((e) => {
        return {
          node: e,
          bounds: (e as HTMLElement).getBoundingClientRect(),
        };
      })
      .filter((e) => e.node !== activeElement)
      .filter((e) => y >= e.bounds.bottom)
      .filter((e) => x <= e.bounds.right && x >= e.bounds.left)
      .map((e) => {
        return {
          elm: e.node as HTMLElement,
          distance: Math.abs(e.bounds.bottom - y),
        };
      });

    mapped.sort((a, b) => b.elm.tabIndex - a.elm.tabIndex || a.distance - b.distance);

    if (mapped.length !== 0) {
      return mapped[0].elm;
    }

    return;
  }

  private downClosestNode(activeElement: HTMLElement, nodes: HTMLElement[]): HTMLElement | undefined {
    const [x, y] = this.getElementCenter(activeElement);

    const mapped = nodes
      .map((e) => {
        return {
          node: e,
          bounds: (e as HTMLElement).getBoundingClientRect(),
        };
      })
      .filter((e) => e.node !== activeElement)
      .filter((e) => y <= e.bounds.top)
      .filter((e) => x <= e.bounds.right && x >= e.bounds.left)
      .map((e) => {
        return {
          elm: e.node as HTMLElement,
          distance: Math.abs(e.bounds.top - y),
        };
      });

    mapped.sort((a, b) => b.elm.tabIndex - a.elm.tabIndex || a.distance - b.distance);

    if (mapped.length !== 0) {
      return mapped[0].elm;
    }

    return;
  }

  private getElementCenter(element: HTMLElement): [number, number] {
    const currentDomRect = element.getBoundingClientRect();
    const x = currentDomRect.left + currentDomRect.width / 2;
    const y = currentDomRect.top + currentDomRect.height / 2;

    return [x, y];
  }
}

function focusAndScrollTo(oldFocus: (options?: FocusOptions | undefined) => void, scrollBehavior: CustomScrollBehaviorCallback<void> | undefined) {
  return function(this: HTMLElement, options?: FocusOptions | undefined) {
    const focusOptions = options ?? { preventScroll: true };
    oldFocus.apply(this, [focusOptions]);
    scrollIntoView(this, {
      scrollMode: 'always',
      // @ts-ignore
      behavior: scrollBehavior,
      block: 'center',
      inline: 'start',
    });
  };
}
